/**
 * Pure pivot-table transform (S5) — groups raw query rows by a row/column
 * pair and aggregates the value column into each cell. Reuses the same
 * `aggregate`/`toNumber`/`formatXValue` primitives the charts feature already
 * uses (S3 `ChartTileView` consumes the sibling `transformChartData`), so
 * pivot cells behave identically to chart aggregation for the same inputs.
 *
 * Column keys are capped at `PIVOT_MAX_COLUMNS` distinct values (first-seen
 * order) — beyond the cap, `truncated: true` is set and the excess columns'
 * values are excluded from cells/totals so column totals stay consistent
 * with what's actually rendered.
 */
import { aggregate, formatXValue, toNumber } from '../../charts/utils/chartDataShaping';
import type { AggregationFunction, ColumnTypeInfo } from '../../charts/types/chartTypes';
import { resolveChartColumnRef } from '../../charts/utils/chartColumnRefs';

export const PIVOT_MAX_COLUMNS = 50;

export type PivotTransformConfig = {
  rowColumn: string;
  columnColumn: string;
  valueColumn: string;
  aggregation: AggregationFunction;
};

/** A pivot tile's column-reference fields, as consumed by `resolvePivotTileRefs`. */
export type PivotTileColumnRefsInput = {
  rowColumn: string;
  columnColumn: string;
  valueColumn: string;
};

/**
 * Resolves a pivot tile's persisted `rowColumn`/`columnColumn`/`valueColumn`
 * against the CURRENT result set's columns — the same rule chart/stat tile
 * configs use (exact row KEY wins, else a NAME that matches exactly one
 * column; ambiguous or not-found references are left as-is so `pivotTransform`
 * degrades the same way it always has — an unmatched column reads as
 * every-row-undefined, an empty pivot). This is the ONE seam the live view,
 * the add-tile preview, and the HTML snapshot export all call so they can
 * never disagree about which columns a pivot tile reads.
 */
export function resolvePivotTileRefs(
  tile: PivotTileColumnRefsInput,
  columns: ColumnTypeInfo[],
): PivotTileColumnRefsInput {
  const refColumns = columns.map((c) => ({ key: c.key, name: c.name }));
  return {
    rowColumn: resolveChartColumnRef(tile.rowColumn, refColumns),
    columnColumn: resolveChartColumnRef(tile.columnColumn, refColumns),
    valueColumn: resolveChartColumnRef(tile.valueColumn, refColumns),
  };
}

export type PivotTransformRow = {
  rowKey: string;
  cells: (number | null)[];
  total: number;
};

export type PivotTransformResult = {
  columnKeys: string[];
  rows: PivotTransformRow[];
  columnTotals: number[];
  grandTotal: number;
  truncated: boolean;
};

export function pivotTransform(
  rows: Record<string, unknown>[],
  config: PivotTransformConfig,
): PivotTransformResult {
  const { rowColumn, columnColumn, valueColumn, aggregation } = config;

  // Pass 1: discover column keys in first-seen order, then cap.
  const columnKeyOrder: string[] = [];
  const seenColumns = new Set<string>();
  for (const row of rows) {
    const key = formatXValue(row[columnColumn]);
    if (!seenColumns.has(key)) {
      seenColumns.add(key);
      columnKeyOrder.push(key);
    }
  }
  const truncated = columnKeyOrder.length > PIVOT_MAX_COLUMNS;
  const columnKeys = truncated ? columnKeyOrder.slice(0, PIVOT_MAX_COLUMNS) : columnKeyOrder;
  const includedColumns = new Set(columnKeys);

  // Pass 2: bucket values by rowKey -> columnKey, preserving row order;
  // values from excluded (truncated) columns never enter any bucket.
  const rowOrder: string[] = [];
  const cellValues = new Map<string, Map<string, (number | null)[]>>();
  for (const row of rows) {
    const colKey = formatXValue(row[columnColumn]);
    if (!includedColumns.has(colKey)) continue;
    const rowKey = formatXValue(row[rowColumn]);
    if (!cellValues.has(rowKey)) {
      cellValues.set(rowKey, new Map());
      rowOrder.push(rowKey);
    }
    const rowMap = cellValues.get(rowKey)!;
    if (!rowMap.has(colKey)) rowMap.set(colKey, []);
    rowMap.get(colKey)!.push(toNumber(row[valueColumn]));
  }

  const pivotRows: PivotTransformRow[] = rowOrder.map((rowKey) => {
    const rowMap = cellValues.get(rowKey)!;
    const cells = columnKeys.map((colKey) => aggregate(rowMap.get(colKey) ?? [], aggregation));
    const rowValues = columnKeys.flatMap((colKey) => rowMap.get(colKey) ?? []);
    return { rowKey, cells, total: aggregate(rowValues, aggregation) ?? 0 };
  });

  const columnTotals = columnKeys.map((colKey) => {
    const values = rowOrder.flatMap((rowKey) => cellValues.get(rowKey)!.get(colKey) ?? []);
    return aggregate(values, aggregation) ?? 0;
  });

  const allValues = rowOrder.flatMap((rowKey) =>
    columnKeys.flatMap((colKey) => cellValues.get(rowKey)!.get(colKey) ?? []),
  );
  const grandTotal = aggregate(allValues, aggregation) ?? 0;

  return { columnKeys, rows: pivotRows, columnTotals, grandTotal, truncated };
}
